import { noop, isEmpty } from 'underscore';
import React, { useCallback, useContext, useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { withValidation, withError, ValidationGroup } from '@bingads-webui-react/validation';
import {
  Field,
  Input,
  Button,
  MenuList,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
} from '@fluentui/react-components';

import { validateSchema, getWebsiteError, getBusinessError } from './schema';
import { SparkleIcon } from './icons';
import { getWebsitePromptInputsStyles } from '../styles/website-prompt-inputs-styles';

const ValidatedInput = withError(withValidation(props => (<Input {...props} ref={props.websiteInputRef} />)));

export const WebsitePromptInputs = ({
  website,
  setWebsite,
  prompt,
  setPrompt,
  websiteInputRef,
  setIsValid,
  alwaysShowError,
  showPrompt,
  promptSuggestions,
  onRefreshClick,
  logger,
}) => {
  const i18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);
  const classes = useDefaultStyles(getWebsitePromptInputsStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);

  const [isShowingWebsiteSummary, setIsShowingWebsiteSummary] = useState(!!website);
  const [isShowingPromptSummary, setIsShowingPromptSummary] = useState(!!prompt);
  const [websiteError, setWebsiteError] = useState('');
  const [promptError, setPromptError] = useState('');

  const isShowingSummary = isShowingWebsiteSummary && isShowingPromptSummary;

  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);

  const currentPromptSuggestions = useMemo(() => {
    if (!prompt || isEmpty(promptSuggestions)) {
      return promptSuggestions;
    }
    return promptSuggestions.filter(suggestion => suggestion.toLowerCase().indexOf(prompt.toLowerCase()) === 0);
  }, [prompt, promptSuggestions]);

  const expandSuggestionList = useCallback(() => {
    setExpanded(true);
  }, []);

  const onPromptInputKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setExpanded(false);
    }
  }, []);

  useEffect(() => {
    const handleBlur = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setTimeout(() => {
          setExpanded(false);
        }, 10);
      }
    };
    document.addEventListener('click', handleBlur);
    return () => {
      document.removeEventListener('click', handleBlur);
    };
  }, []);

  const onWebsiteChange = useCallback((e) => {
    setWebsite(e.target.value);
  }, [setWebsite]);

  const onPromptChange = useCallback((e) => {
    setPrompt(e.target.value);
    setExpanded(true);
  }, [setPrompt]);

  const onValidate = useCallback((e) => {
    setIsValid(e.isValid);
  }, [setIsValid]);

  const onMenuItemClick = useCallback((suggestion) => {
    logger.trace(`[WebsitePromptInputs] Suggestion selected: ${suggestion}`);
    setPrompt(suggestion);
    setExpanded(false);
  }, [logger, setPrompt]);

  const onReviseClick = useCallback(() => {
    logger.trace('[WebsitePromptInputs] Revise clicked');
    setIsShowingWebsiteSummary(false);
    setIsShowingPromptSummary(false);
  }, [logger]);

  const websitePart = useMemo(() => (
    <Field
      className={classNames('website-field', classes.websiteField)}
      label={i18n.getString(_TL_('Your website'))}
      validationMessage={alwaysShowError && websiteError}
    >
      <ValidatedInput
        id="website-input"
        websiteInputRef={websiteInputRef}
        value={website}
        onChange={onWebsiteChange}
        placeholder={i18n.getString(_TL_('Enter your website url'))}
        validationProps={{
          field: 'website',
          errorProcessor: getWebsiteError(i18n, setWebsiteError),
          alwaysShowError,
          debounce: 700,
        }}
      />
    </Field>
  ), [alwaysShowError, classes.websiteField, i18n, onWebsiteChange, website, websiteError, websiteInputRef]);

  const promptPart = useMemo(() => {
    if (!showPrompt) {
      return null;
    }
    return (
      <Field
        className={classNames('prompt-field', classes.promptField)}
        label={i18n.getString(_TL_('What\'s your advertising about'))}
        validationMessage={alwaysShowError && promptError}
      >
        <div ref={containerRef} >
          <ValidatedInput
            id="prompt-input"
            value={prompt}
            onChange={onPromptChange}
            onClick={expandSuggestionList}
            onFocus={expandSuggestionList}
            onKeyDown={onPromptInputKeyDown}
            placeholder={i18n.getString(_TL_('Tell us about this advertising (i.e. Holiday sale)'))}
            validationProps={{
              field: 'business',
              errorProcessor: getBusinessError(i18n, setPromptError),
            }}
          />
          {expanded && currentPromptSuggestions && currentPromptSuggestions.length > 0 &&
            <div className={classNames('prompt-menu-popup', classes.popup)}>
              <div className={classNames('prompt-menu', classes.promptMenu)}>
                <MenuList>
                  <MenuGroup>
                    <MenuGroupHeader>
                      <SparkleIcon />
                      <span className="suggestion-header">{i18n.getString(_TL_('Recommendations from your Final URL'))}</span>
                    </MenuGroupHeader>
                    {currentPromptSuggestions.map(suggestion => (
                      <MenuItem
                        className="suggestion-item"
                        key={suggestion}
                        onClick={() => onMenuItemClick(suggestion)}
                        ariaLabel={suggestion}
                      >
                        {suggestion}
                      </MenuItem>
                    ))}
                  </MenuGroup>
                </MenuList>
              </div>
            </div>
          }
        </div>
      </Field>
    );
  }, [alwaysShowError, classes.popup, classes.promptField, classes.promptMenu,
    currentPromptSuggestions, expandSuggestionList, expanded, i18n, onMenuItemClick,
    onPromptChange, onPromptInputKeyDown, prompt, promptError, showPrompt]);

  if (isShowingSummary) {
    return (
      <React.Fragment>
        <div className={classNames(
          'website-prompt-container',
          classes.container,
          classes.summaryContainer,
          classes[`summaryContainer-${displayMode}`]
        )}
        >
          <div className="summary-text">{prompt ? `${website} | ${prompt}` : website}</div>
        </div>
        {onReviseClick &&
          <div className={classes.reviseButtonContainer}>
            <Button
              className="revise-button"
              onClick={onReviseClick}
            >
              {i18n.getString(_TL_('Revise'))}
            </Button>
          </div>
        }
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className={classNames('website-prompt-container', classes.container, classes[`container-${displayMode}`])}>
        <ValidationGroup
          validationProps={{
            schema: validateSchema,
            i18n: legacyI18n,
            onValidate,
          }}
        >
          {websitePart}
          {showPrompt && <div className={classes.divider} />}
          {promptPart}
        </ValidationGroup>
      </div>
      {onRefreshClick &&
        <div className={classes.refreshButtonContainer}>
          <Button
            className="refresh-button"
            onClick={onRefreshClick}
          >
            {i18n.getString(_TL_('Refresh the ads'))}
          </Button>
        </div>
      }
    </React.Fragment>
  );
};

WebsitePromptInputs.propTypes = {
  website: PropTypes.string.isRequired,
  setWebsite: PropTypes.func.isRequired,
  prompt: PropTypes.string,
  setPrompt: PropTypes.func,
  websiteInputRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }),
  setIsValid: PropTypes.func.isRequired,
  alwaysShowError: PropTypes.bool.isRequired,
  showPrompt: PropTypes.bool.isRequired,
  promptSuggestions: PropTypes.arrayOf(PropTypes.string),
  onRefreshClick: PropTypes.func,
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

WebsitePromptInputs.defaultProps = {
  prompt: '',
  setPrompt: noop,
  websiteInputRef: undefined,
  promptSuggestions: undefined,
  onRefreshClick: undefined,
  logger: {
    trace: noop,
  },
};
